import type {ServerError} from '@logistics-frontend/core/utils/network'
import {useTransition} from '@yandex-delivery/blocks'
import {useCallback, useId} from 'react'

import {useUserSettingsContext} from 'src/shared/contexts'
import type {OptimizeRouteResultResponse, OptimizeRouteStartRequest} from 'src/shared/types'

import {fetchOptimizedRoute} from '../utils/fetch-optimize-route'

type CallbackHandlers = {
  onSuccess: (result?: OptimizeRouteResultResponse) => void
  onError: VoidFunction
  onPending: VoidFunction
}

export function useFetchOptimizedRoute() {
  const key = useId()
  const {clientId = '', country, makeApiUrl} = useUserSettingsContext()
  const [{payload: optimisedRoute, isPending: isOptimisedRoutePending}, startTransition] = useTransition<
    OptimizeRouteResultResponse,
    ServerError
  >(undefined, {
    key: `optimised-route-${key}`,
  })

  const fetchOptimisedRouteHandler = useCallback(
    (body: OptimizeRouteStartRequest, handlers: CallbackHandlers) => {
      handlers.onPending()

      startTransition(
        () => {
          return fetchOptimizedRoute(clientId, body, country, makeApiUrl)
        },
        {
          onCommit: response => {
            handlers.onSuccess(response)
          },
          onError: () => {
            handlers.onError()
          },
        },
      )
    },
    [country, makeApiUrl, clientId],
  )

  return {
    optimisedRoute,
    isOptimisedRoutePending,
    fetchOptimisedRouteHandler,
  }
}
